import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { apiFetch, buildQuery } from '../utils/api';
import { PageHeader, Badge, Loading, formatCurrency, formatDate, ouvrirDocument, fermerDocument, libelleArchiveFiche, ouvrirApercuDevis, ouvrirApercuConvention, BackButton, BoutonRegenerer, niveauMessage, couleurMessage, styleMessage } from '../components/Shared';

const API_BASE = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;
const UNITES = ['Stagiaire', 'Groupe', 'Heure', 'Forfait', 'Jour'];

// ─── Modal modification devis ────────────────────────────────
function ModalModifier({ devis, onSave, onClose }) {
  const { data: formationsData } = useApi('/formations', { limit: 500 }, []);
  const { data: clientsData }    = useApi('/clients',    { limit: 500 }, []);
  const [form, setForm] = useState({
    client_id: devis.client_id || '',
    client_name: devis.client_name || '',
    email_client: devis.email_client || '',
    code_formation: devis.code_formation || '',
    intitule_formation: devis.intitule_formation || '',
    unite: devis.unite || 'Stagiaire',
    qte: devis.qte || 1,
    pu_ht: devis.pu_ht || '',
    remise: devis.remise || 0,
    raw_date_text: devis.raw_date_text || '',
    start_date: devis.start_date ? devis.start_date.split('T')[0] : '',
    end_date: devis.end_date ? devis.end_date.split('T')[0] : '',
    horaires_texte: devis.horaires_texte || devis.horaires_ref || '',
    lieu: devis.lieu || '',
    duree_heures: devis.duree_heures || '',
    prerequis: devis.prerequis || '',
    date_demande: devis.date_demande ? devis.date_demande.split('T')[0] : '',
    date_validite: devis.date_validite ? devis.date_validite.split('T')[0] : '',
    statut_devis: devis.statut_devis || 'Brouillon',
    notes_internes: devis.notes_internes || '',
    note_devis: devis.note_devis || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formationInputVal, setFormationInputVal] = useState('');
  const [showFormationDrop, setShowFormationDrop] = useState(false);

  // Synchroniser l'affichage du champ formation au chargement du modal
  useEffect(() => {
    if (!form.code_formation || !formationsData?.data) return;
    const fo = formationsData.data.find(f => f.formation_id === parseInt(form.code_formation));
    if (fo) setFormationInputVal(`[${fo.numero || fo.formation_id}] ${fo.intitule}`);
  }, [form.code_formation, formationsData]);

  // Multi-sessions disponibles (dates proposées au client)
  const [sessionsSelectionnees, setSessionsSelectionnees] = useState(devis.sessions_possibles || []);
  const [formationSessionsModal, setFormationSessionsModal] = useState([]);
  const [sessionsOpen,          setSessionsOpen]          = useState(false);

  // Pré-charger les sessions quand la formation est connue/change
  useEffect(() => {
    if (!form.code_formation) { setFormationSessionsModal([]); return; }
    apiFetch(`/sessions${buildQuery({ formation_id: form.code_formation, limit: 100, date_from: new Date().toISOString().split('T')[0] })}`)
      .then(d => setFormationSessionsModal(d.data || []))
      .catch(() => setFormationSessionsModal([]));
  }, [form.code_formation]);

  function toggleSession(s) {
    const already = sessionsSelectionnees.some(x => x.session_id === s.session_id);
    setSessionsSelectionnees(already
      ? sessionsSelectionnees.filter(x => x.session_id !== s.session_id)
      : [...sessionsSelectionnees, s]
    );
  }

  function formatLieu(lieu) {
    if (!lieu) return '—';
    const first = lieu.split(/\s*[–\-]\s*/)[0].trim();
    return first.toLowerCase().startsWith('cej') ? 'CEJ' : (first || '—');
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalHt = ((parseFloat(form.pu_ht) || 0) * (parseFloat(form.qte) || 1)) - (parseFloat(form.remise) || 0);

  const inputStyle = {
    width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
  };

  function champ(label, children) {
    return (
      <div style={{ marginBottom: '0.7rem' }}>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>{label}</label>
        {children}
      </div>
    );
  }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      await apiFetch(`/devis/${devis.devis_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          qte: parseFloat(form.qte) || 1,
          pu_ht: parseFloat(form.pu_ht) || 0,
          remise: parseFloat(form.remise) || 0,
          net_ht: totalHt,
          total_ht: totalHt,
          duree_heures: parseFloat(form.duree_heures) || null,
          session_ids: sessionsSelectionnees.map(s => s.session_id),
        }),
      });
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '680px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>✏ Modifier — {devis.devis_id}</h3>

        <div className="grid-2" style={{ gap: '1rem' }}>
          {/* Colonne gauche */}
          <div>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Client</p>
            {champ('Sélectionner un client', (
              <select
                value={form.client_id || ''}
                onChange={e => {
                  const client = clientsData?.data?.find(c => String(c.client_id) === e.target.value);
                  setForm(f => ({
                    ...f,
                    client_id:    e.target.value,
                    client_name:  client?.nom_client    || f.client_name,
                    email_client: client?.contact_email || f.email_client,
                  }));
                }}
                style={inputStyle}
              >
                <option value="">— Choisir un client —</option>
                {clientsData?.data?.map(c => (
                  <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>
                ))}
              </select>
            ))}
            {champ('Nom client', <input type="text" value={form.client_name} onChange={e => set('client_name', e.target.value)} style={inputStyle} />)}
            {champ('Email client', <input type="email" value={form.email_client} onChange={e => set('email_client', e.target.value)} style={inputStyle} />)}

            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Formation</p>
            {champ('Formation du catalogue', (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={formationInputVal}
                  onChange={e => { setFormationInputVal(e.target.value); setShowFormationDrop(true); }}
                  onFocus={() => setShowFormationDrop(true)}
                  onBlur={() => setTimeout(() => setShowFormationDrop(false), 150)}
                  placeholder="Rechercher par N° ou nom…"
                  style={inputStyle}
                />
                {showFormationDrop && (
                  <div style={{ position: 'absolute', zIndex: 200, width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxHeight: '240px', overflowY: 'auto', marginTop: '2px' }}>
                    {formationInputVal && (
                      <div onMouseDown={() => { setForm(prev => ({ ...prev, code_formation: null, intitule_formation: '' })); setFormationInputVal(''); setShowFormationDrop(false); }}
                        style={{ padding: '0.5rem 0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                        ✕ Effacer la sélection
                      </div>
                    )}
                    {(formationsData?.data || [])
                      .filter(f => {
                        const term = formationInputVal.toLowerCase();
                        return !term || String(f.numero || f.formation_id).toLowerCase().includes(term) || f.intitule?.toLowerCase().includes(term);
                      })
                      .map(f => (
                        <div key={f.formation_id}
                          onMouseDown={() => {
                            setForm(prev => ({
                              ...prev,
                              code_formation: f.formation_id,
                              intitule_formation: f.intitule || prev.intitule_formation,
                              duree_heures: f.duree_heures || prev.duree_heures,
                              prerequis: f.prerequis || prev.prerequis,
                            }));
                            setFormationInputVal(`[${f.numero || f.formation_id}] ${f.intitule}`);
                            setShowFormationDrop(false);
                          }}
                          style={{ padding: '0.6rem 0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <strong style={{ color: 'var(--accent-blue)' }}>[{f.numero || f.formation_id}]</strong> {f.intitule}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
            {champ('Intitulé', <input type="text" value={form.intitule_formation} onChange={e => set('intitule_formation', e.target.value)} style={inputStyle} />)}
            {champ('Durée (h)', <input type="number" value={form.duree_heures} onChange={e => set('duree_heures', e.target.value)} style={inputStyle} />)}
            {champ('Prérequis / Aptitude', <input type="text" value={form.prerequis} onChange={e => set('prerequis', e.target.value)} style={inputStyle} />)}
          </div>

          {/* Colonne droite */}
          <div>
            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: '0.7rem', letterSpacing: '0.06em' }}>Tarif</p>

            {/* Boutons prix catalogue */}
            {(() => {
              const formation = formationsData?.data?.find(f =>
                (devis.code_formation && f.formation_id === parseInt(devis.code_formation)) ||
                f.intitule === form.intitule_formation
              );
              const sta = formation ? parseFloat(formation.cout_stagiaire_ht) : 0;
              const grp = formation ? parseFloat(formation.cout_groupe_ht) : 0;
              if (!formation || (!sta && !grp)) return null;
              return (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.7rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Prix catalogue :</span>
                  {sta > 0 && (
                    <button type="button"
                      onClick={() => setForm(f => ({ ...f, pu_ht: sta, unite: 'Stagiaire' }))}
                      style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.8rem',
                        background: form.unite === 'Stagiaire' && parseFloat(form.pu_ht) === sta ? 'var(--accent-blue)' : 'var(--bg-elevated)',
                        color: form.unite === 'Stagiaire' && parseFloat(form.pu_ht) === sta ? '#fff' : 'var(--text-primary)' }}>
                      👤 Stagiaire : {sta.toFixed(2)} €
                    </button>
                  )}
                  {grp > 0 && (
                    <button type="button"
                      onClick={() => setForm(f => ({ ...f, pu_ht: grp, unite: 'Groupe' }))}
                      style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.8rem',
                        background: form.unite === 'Groupe' && parseFloat(form.pu_ht) === grp ? 'var(--accent-blue)' : 'var(--bg-elevated)',
                        color: form.unite === 'Groupe' && parseFloat(form.pu_ht) === grp ? '#fff' : 'var(--text-primary)' }}>
                      👥 Groupe : {grp.toFixed(2)} €
                    </button>
                  )}
                </div>
              );
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '0.5rem' }}>
              {champ('Unité', <select value={form.unite} onChange={e => set('unite', e.target.value)} style={inputStyle}>{UNITES.map(u => <option key={u}>{u}</option>)}</select>)}
              {champ('PU HT (€)', <input type="number" value={form.pu_ht} onChange={e => set('pu_ht', e.target.value)} step="0.01" style={inputStyle} />)}
              {champ('Qté', <input type="number" value={form.qte} onChange={e => set('qte', e.target.value)} min="1" style={inputStyle} />)}
            </div>
            {champ('Remise (€)', <input type="number" value={form.remise} onChange={e => set('remise', e.target.value)} min="0" step="0.01" style={inputStyle} />)}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '6px', padding: '0.6rem 0.8rem', fontSize: '0.9rem', marginBottom: '0.7rem' }}>
              Total HT : <strong style={{ color: 'var(--accent-blue)' }}>{totalHt.toFixed(2)} €</strong>
            </div>

            {/* En-tête accordéon sessions */}
            <div
              onClick={() => form.code_formation && setSessionsOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: form.code_formation ? 'pointer' : 'default', userSelect: 'none', margin: '1rem 0 0.4rem' }}
            >
              <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '0.06em' }}>
                Session(s) disponible(s)
                <span style={{ fontWeight: 400, textTransform: 'none', fontSize: '0.72rem' }}> (dates proposées au client)</span>
                {sessionsSelectionnees.length > 0 && (
                  <span style={{ marginLeft: '0.6rem', background: 'var(--accent-blue)', color: '#fff', borderRadius: '10px', padding: '0.05rem 0.45rem', fontSize: '0.68rem', fontWeight: 700 }}>
                    {sessionsSelectionnees.length} sélectionnée{sessionsSelectionnees.length > 1 ? 's' : ''}
                  </span>
                )}
              </p>
              {form.code_formation && (
                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem 0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1, transform: sessionsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }}>
                  ▼
                </button>
              )}
            </div>

            {/* Tableau checkbox sessions (accordéon) */}
            {!form.code_formation ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.7rem', fontStyle: 'italic' }}>
                Sélectionnez une formation pour afficher les sessions.
              </div>
            ) : !sessionsOpen ? null
            : formationSessionsModal.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginBottom: '0.7rem', fontStyle: 'italic' }}>
                Aucune session pour cette formation.
              </div>
            ) : (() => {
              const fo = formationsData?.data?.find(f => f.formation_id === parseInt(form.code_formation));
              const maxSta = fo?.stagiaires_max ?? '—';
              return (
                <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '0.7rem', fontSize: '0.82rem' }}>
                  {/* En-têtes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 110px 1fr 80px 60px', background: '#eef0f2', borderBottom: '1px solid var(--border)' }}>
                    {['', 'N° session', 'Dates', 'Lieu', 'Inscrits'].map((h, i) => (
                      <div key={i} style={{ padding: '0.4rem 0.5rem', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', color: '#111827', letterSpacing: '0.04em' }}>{h}</div>
                    ))}
                  </div>
                  {/* Lignes */}
                  {formationSessionsModal.map((s, i) => {
                    const checked = sessionsSelectionnees.some(x => x.session_id === s.session_id);
                    const nb = s.nb_inscrits ?? 0;
                    return (
                      <div key={s.session_id}
                        onClick={() => toggleSession(s)}
                        style={{ display: 'grid', gridTemplateColumns: '36px 110px 1fr 80px 60px', cursor: 'pointer',
                          background: checked ? 'rgba(59,130,246,0.07)' : i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-elevated)',
                          borderBottom: i < formationSessionsModal.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.4rem' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleSession(s)}
                            onClick={e => e.stopPropagation()}
                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#3b82f6' }} />
                        </div>
                        <div style={{ padding: '0.45rem 0.5rem', color: 'var(--accent-blue)', fontWeight: 600, wordBreak: 'break-all' }}>{s.session_id}</div>
                        <div style={{ padding: '0.45rem 0.5rem', color: 'var(--text-secondary)' }}>
                          {s.date_debut ? `${new Date(s.date_debut).toLocaleDateString('fr-FR')} → ${new Date(s.date_fin).toLocaleDateString('fr-FR')}` : '—'}
                        </div>
                        <div style={{ padding: '0.45rem 0.5rem', color: 'var(--text-secondary)' }}>{formatLieu(s.lieu)}</div>
                        <div style={{ padding: '0.45rem 0.5rem', color: 'var(--text-secondary)', textAlign: 'center' }}>{nb}/{maxSta}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {champ('Date(s) libre', <input type="text" value={form.raw_date_text} onChange={e => set('raw_date_text', e.target.value)} placeholder="Ex: Le 18/03/2026" style={inputStyle} />)}
            <div className="grid-2" style={{ gap: '0.5rem' }}>
              {champ('Début', <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} style={inputStyle} />)}
              {champ('Fin', <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} style={inputStyle} />)}
            </div>
            {champ('Horaires', <input type="text" value={form.horaires_texte} onChange={e => set('horaires_texte', e.target.value)} style={inputStyle} />)}
            {champ('Lieu', <input type="text" value={form.lieu} onChange={e => set('lieu', e.target.value)} style={inputStyle} />)}

            <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', margin: '1rem 0 0.7rem', letterSpacing: '0.06em' }}>Devis</p>
            <div className="grid-2" style={{ gap: '0.5rem' }}>
              {champ('Date demande', <input type="date" value={form.date_demande} onChange={e => set('date_demande', e.target.value)} style={inputStyle} />)}
              {champ('Validité', <input type="date" value={form.date_validite} onChange={e => set('date_validite', e.target.value)} style={inputStyle} />)}
            </div>
            {champ('Statut', (
              <select value={form.statut_devis} onChange={e => set('statut_devis', e.target.value)} style={inputStyle}>
                {['Brouillon', 'Envoyé', 'Validé', 'Refusé', 'Expiré'].map(s => <option key={s}>{s}</option>)}
              </select>
            ))}
          </div>
        </div>

        {champ('Note (visible sur le devis)', (
          <textarea value={form.note_devis} onChange={e => set('note_devis', e.target.value)} rows={2}
            style={{ ...inputStyle, resize: 'vertical' }} />
        ))}

        {champ('Note interne', (
          <textarea value={form.notes_internes} onChange={e => set('notes_internes', e.target.value)} rows={2}
            style={{ ...inputStyle, resize: 'vertical' }} />
        ))}

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', borderRadius: '6px', padding: '0.5rem 0.8rem', color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale DevisDetail ─────────────────────────────
export default function DevisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, hasRole, user } = useAuth();
  const [adjDevis, setAdjDevis] = useState({ prev: null, next: null });

  const { data, loading, reload } = useApi(`/devis/${id}`);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/devis/${id}/adjacent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAdjDevis(d)).catch(() => {});
  }, [id, token]);
  const [lien, setLien] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [modalModif, setModalModif] = useState(false);
  const [relanceMsg, setRelanceMsg] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [fichesArchivees, setFichesArchivees] = useState([]);
  const [validating, setValidating] = useState(false);
  const [ficheDoublons, setFicheDoublons] = useState(null);
  const [ficheConfirmations, setFicheConfirmations] = useState([]);
  const [ficheExclusions, setFicheExclusions] = useState(new Set()); // indices stagiaires exclus des inscriptions
  const [ficheEcarts, setFicheEcarts] = useState(null);               // différences fiche ↔ inscriptions actuelles
  const [ficheRetraitsSel, setFicheRetraitsSel] = useState(new Set()); // clés « session|stagiaire » cochées dans la fenêtre
  const [ficheRetraitsConf, setFicheRetraitsConf] = useState(undefined); // retraits confirmés (undefined = pas encore demandé)
  const cleEcart = e => `${e.session_actuelle}|${e.stagiaire_id}`;
  // Session & convention
  const [sessions, setSessions] = useState([]);
  const [sessionSearch, setSessionSearch] = useState('');
  const [linkingSession, setLinkingSession] = useState(false);
  // Sessions possibles
  const [sessionsPossiblesDisponibles, setSessionsPossiblesDisponibles] = useState([]);
  const [loadingSP, setLoadingSP] = useState(false);
  const [convention, setConvention] = useState(null);
  const [conventionsAnterieures, setConventionsAnterieures] = useState([]);
  const [autresConventions, setAutresConventions] = useState([]); // conventions des AUTRES sessions du devis (une par session)
  const [convRefresh, setConvRefresh] = useState(0);
  const [convMsg, setConvMsg] = useState('');
  const [convPopup, setConvPopup] = useState(false);
  // Validation interne
  const [showValidInterne, setShowValidInterne] = useState(false);
  const [validOrigine, setValidOrigine] = useState('');
  const [validMsg, setValidMsg] = useState('');
  const [convLien, setConvLien] = useState('');
  const [convEmail, setConvEmail] = useState('');
  const [showConvEmailModal, setShowConvEmailModal] = useState(false);
  const [convSending, setConvSending] = useState(false);
  const [emailBody, setEmailBody] = useState('');
  const [convEmailBody, setConvEmailBody] = useState('');
  const [inclureConvocation, setInclureConvocation] = useState(false);
  const [showFicheEmailModal, setShowFicheEmailModal] = useState(false);
  const [ficheEmailAddr, setFicheEmailAddr] = useState('');
  const [ficheEnvoyant, setFicheEnvoyant] = useState(false);
  const [ficheEnvoiMsg, setFicheEnvoiMsg] = useState('');

  // Charger la fiche d'inscription
  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/devis/${id}/fiche-inscription`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setFiche(d.fiche || null))
      .catch(() => setFiche(null));
  }, [id, token]);

  // Versions précédentes de la fiche (PDF archivés quand le client renvoie une fiche)
  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/devis/${encodeURIComponent(id)}/fiche-archives`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setFichesArchivees(d.data || []))
      .catch(() => setFichesArchivees([]));
  }, [id, token, fiche?.updated_at]);

  // Réinitialiser les exclusions quand la fiche change (nouvelle fiche = tout inclus par défaut)
  useEffect(() => { setFicheExclusions(new Set()); }, [fiche?.id]);

  async function handleValiderFiche(confirmations, retraits = ficheRetraitsConf) {
    if (!fiche?.id) return;
    setValidating(true);
    try {
      const res = await fetch(`${API_BASE}/fiches-inscription/${fiche.id}/valider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          confirmer_doublons: confirmations.length > 0 ? confirmations : undefined,
          stag_exclusions: ficheExclusions.size > 0 ? [...ficheExclusions] : undefined,
          retraits, // undefined : le serveur calcule d'abord les écarts avec les inscriptions actuelles
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur validation');
      if (data.ecarts?.length > 0) {
        setFicheEcarts(data.ecarts);
        // Par défaut : retirer les stagiaires dont la session a changé ; les « absents » restent à cocher volontairement
        setFicheRetraitsSel(new Set(data.ecarts.filter(e => e.type === 'deplace').map(cleEcart)));
        return;
      }
      if (data.doublons?.length > 0) {
        setFicheDoublons(data.doublons);
        setFicheConfirmations([]);
        return;
      }
      setFicheDoublons(null);
      setFicheConfirmations([]);
      setFicheEcarts(null);
      setFicheRetraitsConf(undefined);
      const r2 = await fetch(`${API_BASE}/devis/${id}/fiche-inscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d2 = await r2.json();
      setFiche(d2.fiche || null);
      if (data.a_planifier > 0) {
        alert(`Fiche validée.\n\n${data.a_planifier} stagiaire${data.a_planifier > 1 ? 's' : ''} à planifier (aucune date proposée ne convenait) : ${data.a_planifier > 1 ? 'ils sont créés' : 'il est créé'} dans la base mais non inscrit${data.a_planifier > 1 ? 's' : ''}.\n\nIls figurent dans la rubrique « Stagiaires à planifier » du tableau de bord.`);
      }
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setValidating(false);
    }
  }

  async function supprimerFiche() {
    if (!fiche?.id) return;
    const validee = fiche.statut === 'validee';
    const message = `Supprimer la fiche d'inscription du devis ${id} ?\n\n`
      + (validee
        ? `⚠️ Cette fiche a déjà été VALIDÉE : les stagiaires créés et les inscriptions déjà faites sont conservés, mais la fiche ne sera plus utilisée pour inscrire les stagiaires à la signature de la convention.\n\n`
        : '')
      + `Une copie PDF de la fiche est conservée dans les versions précédentes. Le client pourra renvoyer une nouvelle fiche avec son lien.`;
    if (!window.confirm(message)) return;
    try {
      const res = await fetch(`${API_BASE}/fiches-inscription/${fiche.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Erreur lors de la suppression');
      setFiche(null);
      setFichesArchivees([]);
      const ra = await fetch(`${API_BASE}/devis/${encodeURIComponent(id)}/fiche-archives`, { headers: { Authorization: `Bearer ${token}` } });
      setFichesArchivees((await ra.json().catch(() => ({}))).data || []);
      if (d.archivee === false) alert('Fiche supprimée, mais la copie PDF n\'a pas pu être créée.');
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  // Charger la convention liée au devis
  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/conventions?search=${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        // La liste est triée de la plus récente à la plus ancienne : la 1re est la convention courante
        // Une convention par session : les « précédentes » sont les anciennes versions de la MÊME session ;
        // les conventions des autres sessions du devis sont listées à part
        const duDevis = (d.data || []).filter(c => c.devis_id === id);
        const courante = duDevis[0] || null;
        const memeSession = duDevis.slice(1).filter(c => (c.session_id || null) === (courante?.session_id || null));
        setConvention(courante);
        setConventionsAnterieures(memeSession);
        setAutresConventions(duDevis.slice(1).filter(c => !memeSession.includes(c)));
      })
      .catch(() => {});
  }, [id, token, convRefresh]);

  // Polling auto toutes les 15s si la convention est envoyée (en attente de signature)
  useEffect(() => {
    if (!convention || convention.statut === 'Validée' || !convention.convention_id) return;
    const interval = setInterval(async () => {
      try {
        const rc = await fetch(`${API_BASE}/conventions/${convention.convention_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!rc.ok) return;
        const updated = await rc.json();
        setConvention(prev => {
          if (updated.statut === 'Validée' && prev?.statut !== 'Validée') {
            setConvPopup(true);
            reload(); // rafraîchir le devis pour afficher signataire_nom dans "Accord client"
          }
          return updated;
        });
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [convention?.convention_id, convention?.statut, token]);

  // Charger les sessions disponibles pour "sessions possibles"
  useEffect(() => {
    if (!data) return;
    const devisObj = Array.isArray(data?.data) ? data.data[0] : data;
    const formationId = devisObj?.code_formation ? parseInt(devisObj.code_formation) : null;
    if (!formationId) return;
    const dateFrom = new Date().toISOString().split('T')[0];
    fetch(`${API_BASE}/sessions?formation_id=${formationId}&limit=100&date_from=${dateFrom}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setSessionsPossiblesDisponibles(d.data || []))
      .catch(() => {});
  }, [data, token]);

  // Charger les sessions pour la recherche — filtrées sur la formation du devis
  useEffect(() => {
    if (!sessionSearch || sessionSearch.length < 2) { setSessions([]); return; }
    const devisObj = Array.isArray(data?.data) ? data.data[0] : data;
    const formationId = devisObj?.code_formation ? parseInt(devisObj.code_formation) : null;
    const params = new URLSearchParams();
    params.set('search', sessionSearch);
    params.set('limit', '20');
    if (formationId && !isNaN(formationId)) params.set('formation_id', formationId);
    fetch(`${API_BASE}/sessions?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        let list = d.data || [];
        if (formationId && !isNaN(formationId)) {
          list = list.filter(s => parseInt(s.formation_id) === formationId);
        }
        setSessions(list);
      })
      .catch(() => {});
  }, [sessionSearch, token, data]);

  async function validerInterne() {
    if (!validOrigine.trim()) return;
    setValidating(true); setValidMsg('');
    try {
      const r = await fetch(`${API_BASE}/devis/${id}/valider-interne`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ origine: validOrigine }),
      });
      const d = await r.json();
      if (!r.ok) { setValidMsg('Erreur : ' + (d.error || 'inconnue')); return; }
      setShowValidInterne(false);
      setValidOrigine('');
      reload();
    } catch (e) { setValidMsg('Erreur réseau'); }
    finally { setValidating(false); }
  }

  async function ajouterSession(sessionId) {
    setLinkingSession(true);
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`);
      setSessionSearch('');
      setSessions([]);
      reload();
    } catch (e) {
      alert(`Erreur lors de l'association : ${e.message}`);
    } finally {
      setLinkingSession(false);
    }
  }

  async function retirerSession(sessionId) {
    setLinkingSession(true);
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      reload();
    } catch (e) {
      alert(`Erreur : ${e.message}`);
    } finally {
      setLinkingSession(false);
    }
  }

  async function ajouterSessionPossible(sessionId) {
    setLoadingSP(true);
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/sessions-possibles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      reload();
    } catch (e) { alert(e.message); }
    finally { setLoadingSP(false); }
  }

  async function retirerSessionPossible(sessionId) {
    // Confirmation si devis validé ou envoyé
    const statut = devis.statut_devis;
    if (statut === 'Validé') {
      if (!window.confirm(
        'Ce devis a été validé par le client.\n\nSi vous retirez cette session, le choix de session de la fiche d\'inscription sera réinitialisé et le client devra en choisir une nouvelle.\n\nConfirmez-vous ?'
      )) return;
    } else if (statut === 'Envoyé') {
      if (!window.confirm(
        'Ce devis a déjà été envoyé au client.\n\nÊtes-vous sûr de vouloir retirer cette session ?'
      )) return;
    }
    setLoadingSP(true);
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/sessions-possibles/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error);
      }
      reload();
    } catch (e) { alert(e.message); }
    finally { setLoadingSP(false); }
  }

  async function creerConvention() {
    setConvMsg('');
    try {
      const r = await fetch(`${API_BASE}/conventions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ devis_id: id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvention(d);
      setConvMsg('✓ Convention créée');
    } catch(e) { setConvMsg(`❌ ${e.message}`); }
  }

  async function regenererConvention() {
    setConvMsg('');
    try {
      const r = await fetch(`${API_BASE}/conventions/${convention.convention_id}/regenerer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvention(prev => ({ ...prev, ...d }));
      setConvMsg(`✓ Convention régénérée — session ${d.session_id}${d.lien_invalide ? ' (ancien lien invalidé)' : ''}`);
    } catch (e) { setConvMsg(`❌ ${e.message}`); }
  }

  async function nouvelleConvention() {
    setConvMsg('');
    try {
      const r = await fetch(`${API_BASE}/conventions/${convention.convention_id}/nouvelle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvRefresh(n => n + 1);
      setConvMsg('✓ Convention signée archivée — nouvelle convention (brouillon) créée');
    } catch (e) { setConvMsg(`❌ ${e.message}`); }
  }

  async function genererLienConvention() {
    setConvSending(true); setConvMsg('');
    try {
      const r = await fetch(`${API_BASE}/conventions/${convention.convention_id}/generer-lien`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_client: devis.email_client }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvLien(d.lien);
      setConvEmail(devis.email_client || '');
      setConvMsg('✓ Lien convention généré');
      // Recharger la convention
      const rc = await fetch(`${API_BASE}/conventions/${convention.convention_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setConvention(await rc.json());
    } catch(e) { setConvMsg(`❌ ${e.message}`); }
    finally { setConvSending(false); }
  }

  async function genererLienConventionEtEnvoyer() {
    setConvSending(true); setConvMsg('');
    try {
      const r = await fetch(`${API_BASE}/conventions/${convention.convention_id}/generer-lien`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_client: devis.email_client }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvLien(d.lien);
      setConvEmail(devis.email_client || '');
      setConvEmailBody(`Bonjour,\n\nVeuillez trouver ci-dessous le lien pour consulter et signer votre convention de formation :\n\n${devis.intitule_formation}`);
      setInclureConvocation(false);
      const rc = await fetch(`${API_BASE}/conventions/${convention.convention_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setConvention(await rc.json());
      setShowConvEmailModal(true);
    } catch(e) { setConvMsg(`❌ ${e.message}`); }
    finally { setConvSending(false); }
  }

  // Helper : met à jour la note pièces jointes en fin de message convention
  function buildCorpsConvention(base, avecConvocation) {
    const cleaned = base.replace(/\n\n📎[^\n]*/g, '').trimEnd();
    const note = avecConvocation
      ? '\n\n📎 Documents joints : programme de formation, convocation de formation + livret stagiaire.'
      : '\n\n📎 Le programme de formation est joint à cet email.';
    return cleaned + note;
  }

  async function envoyerEmailConvention() {
    if (!convEmail) return;
    setConvSending(true); setConvMsg('');
    try {
      const lienEnvoi = convLien || (convention.lien_token ? `${APP_URL}/convention-client/${convention.lien_token}` : '');
      const r = await fetch(`${API_BASE}/conventions/${convention.convention_id}/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: convEmail, lien: lienEnvoi, message_personnalise: convEmailBody, inclure_convocation: inclureConvocation }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setConvMsg('✓ Convention envoyée' + (d.convocation_jointe ? ' + convocation jointe' : d.programme_joint ? ' + programme joint' : ''));
      setShowConvEmailModal(false);
    } catch(e) { setConvMsg(`❌ ${e.message}`); }
    finally { setConvSending(false); }
  }

  if (loading || !data) return <Loading />;

  const devis = Array.isArray(data?.data) ? data.data[0] : data;
  if (!devis) return <div className="page"><p>Devis introuvable</p></div>;

  const totalHt = parseFloat(devis.total_ht || 0);
  const tva = totalHt * 0.2;
  const totalTtc = totalHt + tva;

  const estExpire = devis.date_validite && new Date(devis.date_validite) < new Date() && devis.statut_devis !== 'Validé';

  async function genererLien() {
    setSending(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/generer-lien`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_client: devis.email_client }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setLien(d.lien);
      setEmail(devis.email_client || '');
      setMsg('✓ Lien généré avec succès');
      reload();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSending(false); }
  }

  async function genererEtEnvoyer() {
    setSending(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/generer-lien`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_client: devis.email_client, ne_pas_marquer_envoye: true }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setLien(d.lien);
      setEmail(devis.email_client || '');
      setEmailBody(`Bonjour,\n\nVeuillez trouver ci-dessous le lien pour consulter et valider votre devis :\n\nDevis N° ${devis.devis_id} — ${devis.intitule_formation}`);
      reload();
      setShowEmailModal(true);
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSending(false); }
  }

  async function envoyerEmail() {
    if (!email) return setMsg('❌ Saisissez un email');
    setSending(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/envoyer-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, lien: lien || `${APP_URL}/devis-client/${devis.lien_token}`, message_personnalise: emailBody }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setMsg('✓ Email envoyé' + (d.programme_joint ? ' + programme joint' : ''));
      setShowEmailModal(false);
      reload();
    } catch (e) { setMsg(`❌ ${e.message}`); }
    finally { setSending(false); }
  }

  async function envoyerRelance() {
    if (!email) return;
    setSending(true); setRelanceMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/relancer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, lien: lien || `${APP_URL}/devis-client/${devis.lien_token}` }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setRelanceMsg('✓ Relance envoyée');
      setTimeout(() => { setShowRelanceModal(false); setRelanceMsg(''); }, 2000);
    } catch (e) { setRelanceMsg(`❌ ${e.message}`); }
    finally { setSending(false); }
  }

  async function renvoyerLienFiche() {
    if (!ficheEmailAddr) return setFicheEnvoiMsg('❌ Saisissez un email');
    setFicheEnvoyant(true); setFicheEnvoiMsg('');
    try {
      const res = await fetch(`${API_BASE}/devis/${id}/envoyer-fiche`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: ficheEmailAddr }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setFicheEnvoiMsg('✓ Lien envoyé');
      setTimeout(() => { setShowFicheEmailModal(false); setFicheEnvoiMsg(''); }, 2000);
    } catch (e) { setFicheEnvoiMsg(`❌ ${e.message}`); }
    finally { setFicheEnvoyant(false); }
  }

  function copierLien() {
    const texte = lien || `${APP_URL}/devis-client/${devis.lien_token}`;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(texte).catch(() => _fallbackCopy(texte));
    } else {
      _fallbackCopy(texte);
    }
    setMsg('✓ Lien copié dans le presse-papier');
    setTimeout(() => setMsg(''), 3000);
  }
  function _fallbackCopy(texte) {
    const ta = document.createElement('textarea');
    ta.value = texte; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    try { document.execCommand('copy'); } catch (e) { alert('Lien : ' + texte); }
    document.body.removeChild(ta);
  }

  const lienExistant = devis.lien_token ? `${APP_URL}/devis-client/${devis.lien_token}` : '';
  const lienActif = lien || lienExistant;

  async function handleArchiver() {
    if (!window.confirm(`Archiver le devis "${devis.devis_id}" ?\nIl sera masqué des listes mais pourra être restauré.`)) return;
    setArchiving(true);
    try {
      await apiFetch(`/devis/${id}`, { method: 'DELETE' });
      navigate('/devis');
    } catch (e) { alert(`Erreur : ${e.message}`); setArchiving(false); }
  }

  async function handleRestaurer() {
    setArchiving(true);
    try {
      await apiFetch(`/devis/${id}/restaurer`, { method: 'POST' });
      window.location.reload();
    } catch (e) { alert(`Erreur : ${e.message}`); setArchiving(false); }
  }

  return (
    <div className="page">
      {/* Popup — convention signée */}
      {convPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '2px solid var(--accent-emerald)', borderRadius: '16px', padding: '2.5rem 2rem', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
            <h2 style={{ color: 'var(--accent-emerald)', fontSize: '1.25rem', marginBottom: '0.5rem' }}>Convention signée !</h2>
            <p style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.25rem' }}>
              {convention?.signataire_nom || ''}
            </p>
            {convention?.date_signature && (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: '1.5rem' }}>
                le {new Date(convention.date_signature).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
            )}
            <button className="btn-primary" onClick={() => setConvPopup(false)}>✓ OK</button>
          </div>
        </div>
      )}

      <PageHeader
        title={<span style={{ color: '#E87722', fontWeight: 800 }}>{devis.devis_id}</span>}
        subtitle={devis.intitule_formation}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <BackButton />
            <button className="btn-secondary" onClick={() => navigate(`/devis/${adjDevis.prev}`)}
              disabled={!adjDevis.prev} style={{ opacity: adjDevis.prev ? 1 : 0.35 }}
              title={adjDevis.prev || 'Premier devis'}>◀ Précédent</button>
            <button className="btn-secondary" onClick={() => navigate(`/devis/${adjDevis.next}`)}
              disabled={!adjDevis.next} style={{ opacity: adjDevis.next ? 1 : 0.35 }}
              title={adjDevis.next || 'Dernier devis'}>Suivant ▶</button>
            <button className="btn-secondary" onClick={() => ouvrirApercuDevis(devis.devis_id)}>👁 Aperçu</button>
            {hasRole('utilisateur') && (
              <>
                {devis.actif === false && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉ</span>
                )}
                {devis.statut_devis !== 'Validé' && (
                  <button className="btn-secondary" onClick={() => setModalModif(true)}>✏ Modifier</button>
                )}
                <button className="btn-secondary" onClick={() => navigate(`/devis/nouveau?dupliquer=${id}`)}>⧉ Dupliquer</button>
                {devis.actif !== false ? (
                  <button onClick={handleArchiver} disabled={archiving}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.5)', background: 'transparent', color: '#f87171', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {archiving ? '⏳...' : '🗑 Archiver'}
                  </button>
                ) : (
                  <button onClick={handleRestaurer} disabled={archiving}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--accent-emerald)', background: 'transparent', color: 'var(--accent-emerald)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    {archiving ? '⏳...' : '↩ Restaurer'}
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      {estExpire && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          ⚠ Ce devis a expiré le {formatDate(devis.date_validite)}
        </div>
      )}

      <div className="detail-grid">
        <div className="card">
          <h3 className="card-title">Informations</h3>
          <div className="info-grid">
            <div className="info-row"><label>N° Devis</label><span><strong>{devis.devis_id}</strong></span></div>
            <div className="info-row"><label>Statut</label><span><Badge>{devis.statut_devis}</Badge></span></div>
            <div className="info-row"><label>Client</label>
              <span>
                <strong>{devis.client_name}</strong>
                {devis.client_id && (
                  <button onClick={() => navigate(`/clients/${devis.client_id}`)}
                    style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: 'var(--accent-blue)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
                    → Fiche client
                  </button>
                )}
              </span>
            </div>
            <div className="info-row"><label>Email client</label><span>{devis.email_client || '—'}</span></div>
            <div className="info-row"><label>Formation</label><span>{devis.intitule_formation}</span></div>
            <div className="info-row"><label>Durée</label><span>{devis.duree_heures ? `${devis.duree_heures}h` : '—'}</span></div>
            <div className="info-row"><label>Date(s)</label><span>{devis.raw_date_text || (devis.start_date ? formatDate(devis.start_date) : '—')}</span></div>
            <div className="info-row"><label>Lieu</label><span>{devis.lieu || '—'}</span></div>
            <div className="info-row"><label>Horaires</label><span>{devis.horaires_texte || devis.horaires_ref || '—'}</span></div>
            {devis.prerequis && <div className="info-row"><label>Prérequis / Aptitude</label><span>{devis.prerequis}</span></div>}
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Tarif</h3>
          <div className="info-grid">
            <div className="info-row"><label>Unité</label><span>{devis.unite}</span></div>
            <div className="info-row"><label>Prix unitaire HT</label><span>{formatCurrency(devis.pu_ht)}</span></div>
            <div className="info-row"><label>Quantité</label><span>{devis.qte}</span></div>
            {parseFloat(devis.remise) > 0 && (
              <div className="info-row">
                <label>Remise</label>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>
                  {devis.taux_remise_pct > 0 ? `${devis.taux_remise_pct}% — ` : ''}
                  - {formatCurrency(devis.remise)}
                </span>
              </div>
            )}
            <div className="info-row"><label>Total HT</label><span><strong>{formatCurrency(totalHt)}</strong></span></div>
            <div className="info-row"><label>TVA 20%</label><span>{formatCurrency(tva)}</span></div>
            <div className="info-row"><label>Total TTC</label>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--accent-blue)' }}>{formatCurrency(totalTtc)}</span>
            </div>
          </div>
          <div style={{ marginTop: '0.8rem', paddingTop: '0.8rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <div>Date demande : {formatDate(devis.date_demande)}</div>
            <div style={{ color: estExpire ? '#f87171' : 'inherit' }}>Validité : {formatDate(devis.date_validite)}</div>
          </div>
        </div>
      </div>

      {devis.note_devis && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">Note</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{devis.note_devis}</p>
        </div>
      )}

      {devis.notes_internes && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">Note interne</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{devis.notes_internes}</p>
        </div>
      )}

      {devis.statut_devis === 'Validé' && (
        <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--accent-emerald)', background: 'rgba(16,185,129,0.05)' }}>
          <h3 className="card-title" style={{ color: 'var(--accent-emerald)' }}>
            ✓ Devis validé
            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 400, color: '#64748b' }}>
              {devis.validation_interne_origine ? 'validation interne' : 'validation client'}
            </span>
          </h3>
          <div className="info-grid">
            {devis.validation_interne_origine ? (
              <>
                <div className="info-row"><label>Validé par</label><span><strong>{devis.validation_interne_par}</strong></span></div>
                <div className="info-row"><label>Origine de la validation</label><span>{devis.validation_interne_origine}</span></div>
                <div className="info-row"><label>Date</label><span>{formatDate(devis.date_signature)}</span></div>
              </>
            ) : (
              <>
                <div className="info-row"><label>Signataire</label><span><strong>{devis.signataire_nom}</strong></span></div>
                <div className="info-row"><label>Téléphone</label><span>{devis.signataire_tel}</span></div>
                <div className="info-row"><label>Date de signature</label><span>{formatDate(devis.date_signature)}</span></div>
              </>
            )}
          </div>
        </div>
      )}

      {devis.statut_devis !== 'Validé' && hasRole('utilisateur') && (
        <div className="card" style={{ marginTop: '1rem', borderLeft: '3px solid var(--accent-emerald)' }}>
          <h3 className="card-title">✓ Valider le devis</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {/* Option 1 : lien client */}
            <div style={{ flex: 1, minWidth: 240, padding: '1rem', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.4rem', color: 'var(--text-primary)' }}>
                Option 1 — Validation par le client
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.8rem', lineHeight: 1.5 }}>
                Envoyez un lien au client pour qu'il consulte et signe le devis en ligne.
              </p>
              {/* Lien existant ou génération */}
              {lienActif ? (
                <div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                    <input type="text" readOnly value={lienActif}
                      style={{ flex: 1, padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: '0.78rem', minWidth: 160 }} />
                    <button className="btn-secondary" style={{ fontSize: '0.8rem' }} onClick={copierLien}>📋</button>
                    <button className="btn-primary" style={{ fontSize: '0.8rem' }} onClick={() => {
                      setEmail(devis.email_client || '');
                      setEmailBody(`Bonjour,\n\nVeuillez trouver ci-dessous le lien pour consulter et valider votre devis :\n\nDevis N° ${devis.devis_id} — ${devis.intitule_formation}`);
                      setShowEmailModal(true);
                    }}>✉ Envoyer</button>
                    {devis.statut_devis === 'Envoyé' && (
                      <button className="btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => { setEmail(devis.email_client || ''); setShowRelanceModal(true); }}>↻ Relancer</button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                    {devis.token_expire_at && (
                      <span style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>Expire le {formatDate(devis.token_expire_at)}</span>
                    )}
                    <button className="btn-secondary" onClick={genererLien} disabled={sending} style={{ fontSize: '0.75rem', padding: '0.2rem 0.7rem' }}>
                      {sending ? '⏳' : '↺ Nouveau lien'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn-primary" style={{ fontSize: '0.85rem' }} onClick={genererEtEnvoyer} disabled={sending}>
                  {sending ? '⏳...' : '✉ Envoyer'}
                </button>
              )}
            </div>

            {/* Option 2 : validation interne */}
            <div style={{ flex: 1, minWidth: 240, padding: '1rem', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.4rem', color: 'var(--text-primary)' }}>
                Option 2 — Validation interne
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.8rem', lineHeight: 1.5 }}>
                L'accord a été obtenu directement (téléphone, email, réunion…). Enregistrez l'origine de la validation.
              </p>
              <button onClick={() => {
                const nom = [user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.login || '';
                setValidOrigine(`Accord de ${devis.client_name || ''}${nom ? ` — enregistré par ${nom}` : ''}`);
                setValidMsg('');
                setShowValidInterne(true);
              }}
                style={{ padding: '0.45rem 1rem', borderRadius: '8px', border: 'none', background: '#059669', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                ✓ Enregistrer la validation
              </button>
            </div>
          </div>

          {msg && (
            <p style={{ marginTop: '0.8rem', fontSize: '0.85rem', color: couleurMessage(niveauMessage(msg)) }}>{msg}</p>
          )}
        </div>
      )}

      {/* Fiche d'inscription */}
      <div className="card" style={{ marginTop: '1rem', borderColor: fiche?.statut === 'validee' ? 'var(--accent-emerald)' : fiche?.statut === 'soumise' ? '#f59e0b' : devis.statut_devis === 'Validé' ? 'var(--border)' : undefined }}>
        <h3 className="card-title">
          📋 Fiche d'inscription
          {fiche?.statut === 'validee' && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent-emerald)', background: 'rgba(16,185,129,0.1)', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
              ✅ Validée
            </span>
          )}
          {fiche?.statut === 'soumise' && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
              ⏳ Reçue — en attente de validation
            </span>
          )}
          {!fiche && devis.statut_devis === 'Validé' && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--bg-elevated)', padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
              ⏳ En attente
            </span>
          )}
        </h3>

        {fichesArchivees.length > 0 && (
          <div style={{ marginBottom: '0.75rem', fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Versions précédentes de la fiche (PDF archivé)</div>
            {fichesArchivees.map(a => {
              const quand = a.date ? new Date(a.date).toLocaleString('fr-FR') : a.nom;
              return (
                <div key={a.nom} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
                  <span>{quand} — {libelleArchiveFiche(a.statut)}</span>
                  <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                    onClick={() => ouvrirDocument({ titre: `Fiche d'inscription — version du ${quand}`, url: `/devis/${encodeURIComponent(id)}/fiche-archives/${encodeURIComponent(a.nom)}`, nomFichier: a.nom })}>
                    👁 Aperçu
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal doublons */}
        {ficheDoublons && ficheDoublons.length > 0 && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '1.5rem', maxWidth: 600, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
              <h3 style={{ color: '#f59e0b', marginBottom: '0.5rem' }}>⚠️ Stagiaires déjà enregistrés</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Des correspondances ont été trouvées dans la base. Choisissez l'action pour chaque stagiaire.
              </p>
              {ficheDoublons.map(d => {
                const conf = ficheConfirmations.find(c => c.idx === d.idx);
                return (
                  <div key={d.idx} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--bg-elevated)' }}>
                    <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem', fontSize: '0.88rem' }}>
                      Stagiaire {d.idx + 1} : {d.saisi?.prenom} {d.saisi?.nom_naissance || d.saisi?.nom_usage}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                      <div style={{ background: 'rgba(245,158,11,0.08)', borderRadius: 6, padding: '0.5rem' }}>
                        <strong>Saisi dans la fiche :</strong><br/>
                        {d.saisi?.prenom} {d.saisi?.nom_naissance || d.saisi?.nom_usage}<br/>
                        {d.saisi?.date_naissance ? new Date(d.saisi.date_naissance).toLocaleDateString('fr-FR') : '—'}
                      </div>
                      <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 6, padding: '0.5rem' }}>
                        <strong>Existant ({d.match.stagiaire_id}) :</strong><br/>
                        {d.match.prenoms} {d.match.nom}<br/>
                        {d.match.date_naissance ? new Date(d.match.date_naissance).toLocaleDateString('fr-FR') : '—'}
                      </div>
                    </div>
                    {d.proximite === 'debut' && (
                      <p style={{ fontSize: '0.78rem', color: '#b45309', background: 'rgba(245,158,11,0.12)', borderRadius: 6, padding: '0.4rem 0.6rem', margin: '0 0 0.6rem' }}>
                        ⚠ Prénoms proches mais pas identiques : vérifiez qu'il s'agit bien de la même personne avant de choisir « Mettre à jour l'existant » (le prénom enregistré sera remplacé par celui de la fiche).
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => setFicheConfirmations(prev => [...prev.filter(c => c.idx !== d.idx), { idx: d.idx, action: 'remplacer', stagiaire_id: d.match.stagiaire_id }])}
                        style={{ flex: 1, padding: '0.4rem', borderRadius: 6, border: `2px solid ${conf?.action === 'remplacer' ? 'var(--accent-emerald)' : 'var(--border)'}`, background: conf?.action === 'remplacer' ? 'rgba(16,185,129,0.1)' : 'var(--bg-card)', color: conf?.action === 'remplacer' ? 'var(--accent-emerald)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                        ✅ Mettre à jour l'existant
                      </button>
                      <button onClick={() => setFicheConfirmations(prev => [...prev.filter(c => c.idx !== d.idx), { idx: d.idx, action: 'nouveau', stagiaire_id: null }])}
                        style={{ flex: 1, padding: '0.4rem', borderRadius: 6, border: `2px solid ${conf?.action === 'nouveau' ? 'var(--accent-blue)' : 'var(--border)'}`, background: conf?.action === 'nouveau' ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)', color: conf?.action === 'nouveau' ? 'var(--accent-blue)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                        ➕ Créer un nouveau
                      </button>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => { setFicheDoublons(null); setFicheConfirmations([]); setFicheRetraitsConf(undefined); }}
                  className="btn-secondary" style={{ fontSize: '0.85rem' }}>Annuler</button>
                <button
                  onClick={() => handleValiderFiche(ficheConfirmations)}
                  disabled={ficheConfirmations.length < ficheDoublons.length || validating}
                  className="btn-primary" style={{ fontSize: '0.85rem', opacity: ficheConfirmations.length < ficheDoublons.length ? 0.5 : 1 }}>
                  {validating ? '⏳...' : '✅ Confirmer et valider'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal écarts fiche ↔ inscriptions actuelles */}
        {ficheEcarts && ficheEcarts.length > 0 && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '1.5rem', maxWidth: 620, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
              <h3 style={{ color: '#f59e0b', marginBottom: '0.5rem' }}>⚠️ Différences avec les inscriptions actuelles</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                La fiche ne correspond plus à ce qui est inscrit. Cochez les inscriptions à retirer (sans envoi d'email).
                Un stagiaire « absent de la fiche » peut venir d'un autre devis du même client : il n'est pas coché par défaut.
              </p>
              {ficheEcarts.map(e => {
                const cle = cleEcart(e);
                return (
                  <label key={cle} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.6rem 0.75rem', marginBottom: '0.5rem', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-elevated)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={ficheRetraitsSel.has(cle)} style={{ marginTop: '0.2rem' }}
                      onChange={ev => setFicheRetraitsSel(prev => {
                        const next = new Set(prev);
                        if (ev.target.checked) next.add(cle); else next.delete(cle);
                        return next;
                      })} />
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                      <strong>{e.prenom} {e.nom}</strong> — retirer de <strong>{e.session_actuelle}</strong><br />
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                        {e.type === 'deplace'
                          ? `Session changée : la fiche indique ${e.session_cible}`
                          : 'Inscrit, mais absent de la fiche (remplacé ou retiré par le client)'}
                      </span>
                    </span>
                  </label>
                );
              })}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => { setFicheEcarts(null); setFicheRetraitsConf(undefined); }}
                  className="btn-secondary" style={{ fontSize: '0.85rem' }}>Annuler</button>
                <button
                  onClick={() => {
                    const sel = ficheEcarts.filter(e => ficheRetraitsSel.has(cleEcart(e))).map(e => ({ stagiaire_id: e.stagiaire_id, session_id: e.session_actuelle }));
                    setFicheEcarts(null);
                    setFicheRetraitsConf(sel);
                    handleValiderFiche([], sel);
                  }}
                  disabled={validating}
                  className="btn-primary" style={{ fontSize: '0.85rem' }}>
                  {validating ? '⏳...' : '✅ Valider la fiche'}
                </button>
              </div>
            </div>
          </div>
        )}

        {fiche ? (
          <div>
            <div className="info-grid">
              {fiche.entreprise_raison_sociale && <div className="info-row"><label>Entreprise</label><span>{fiche.entreprise_raison_sociale}</span></div>}
              {fiche.particulier_nom && <div className="info-row"><label>Particulier</label><span>{fiche.particulier_civilite} {fiche.particulier_prenom} {fiche.particulier_nom}</span></div>}
              {fiche.contact_nom && <div className="info-row"><label>Contact inscription</label><span>{fiche.contact_civilite} {fiche.contact_prenom} {fiche.contact_nom} {fiche.contact_tel ? `— ${fiche.contact_tel}` : ''} {fiche.contact_mail ? `— ${fiche.contact_mail}` : ''}</span></div>}
              <div className="info-row"><label>Nb stagiaires</label><span><strong>{Array.isArray(fiche.stagiaires) ? fiche.stagiaires.length : 0}</strong></span></div>
              {fiche.fait_a && <div className="info-row"><label>Fait à</label><span>{fiche.fait_a} le {fiche.fait_le ? new Date(fiche.fait_le).toLocaleDateString('fr-FR') : ''}</span></div>}
            </div>

            {/* ── Panel sessions / inscriptions (visible quand soumise) ── */}
            {fiche.statut === 'soumise' && (() => {
              const stagsValides = (Array.isArray(fiche.stagiaires) ? fiche.stagiaires : [])
                .filter(s => s.nom_naissance || s.nom_usage || s.prenom);
              if (stagsValides.length === 0) return null;
              const sessionsLieesIds = new Set((devis.sessions_liees || []).map(s => s.session_id));
              const sessionsFichesSoumisesIds = new Set(devis.sessions_fiches_soumises || []);
              // Regrouper par session choisie
              const bySession = {};
              stagsValides.forEach((s, idx) => {
                // « AUCUNE » = le client a répondu qu'aucune des dates proposées ne lui convient (stagiaire à planifier)
                const sid = s.session_id === 'AUCUNE' ? '__aplanifier__' : (s.session_id || '__aucune__');
                if (!bySession[sid]) bySession[sid] = [];
                bySession[sid].push({ idx, s });
              });
              return (
                <div style={{ marginTop: '1rem', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ background: 'rgba(59,130,246,0.07)', padding: '0.55rem 0.9rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>📌 Sessions choisies — inscriptions</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Décocher pour exclure un stagiaire de l'inscription à la session</span>
                  </div>
                  <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                    {Object.entries(bySession).map(([sid, stags]) => {
                      const sessInfo = sessionsPossiblesDisponibles.find(s => s.session_id === sid);
                      const isLinked = sessionsLieesIds.has(sid);
                      const isFicheSoumise = !isLinked && sessionsFichesSoumisesIds.has(sid);
                      const nbInscrits = sessInfo?.nb_inscrits ?? '?';
                      const nbMax = sessInfo?.stagiaires_max ?? '?';
                      const full = typeof nbInscrits === 'number' && typeof nbMax === 'number' && nbInscrits >= nbMax;
                      const debut = sessInfo?.date_debut ? new Date(sessInfo.date_debut).toLocaleDateString('fr-FR') : null;
                      const fin   = sessInfo?.date_fin   ? new Date(sessInfo.date_fin).toLocaleDateString('fr-FR')   : null;
                      const canEnroll = sid !== '__aucune__' && sid !== '__aplanifier__';
                      const precisionsClient = sid === '__aplanifier__' ? (stags.find(({ s }) => s.dates_souhaitees)?.s.dates_souhaitees || '') : '';
                      return (
                        <div key={sid} style={{ borderRadius: 6, border: `1px solid ${isLinked ? 'var(--border)' : isFicheSoumise ? 'rgba(59,130,246,0.4)' : 'rgba(245,158,11,0.4)'}`, overflow: 'hidden' }}>
                          {/* En-tête session */}
                          <div style={{ background: isLinked ? 'var(--bg-elevated)' : isFicheSoumise ? 'rgba(59,130,246,0.05)' : 'rgba(245,158,11,0.06)', padding: '0.45rem 0.8rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
                            {sid === '__aplanifier__' ? (
                              <>
                                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#c2410c' }}>🗓 À planifier — aucune date proposée ne convient</span>
                                {precisionsClient && <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>Précisions du client : « {precisionsClient} »</span>}
                              </>
                            ) : sid === '__aucune__' ? (
                              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#9ca3af' }}>⚠ Aucune session choisie</span>
                            ) : (
                              <>
                                <span style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--accent-blue)' }}>{sid}</span>
                                {debut && <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Du {debut}{fin ? ` au ${fin}` : ''}</span>}
                                {sessInfo && (
                                  <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.1rem 0.5rem', borderRadius: 4,
                                    color: full ? '#ef4444' : 'var(--accent-emerald)',
                                    background: full ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)' }}>
                                    {nbInscrits}/{nbMax} inscrits
                                  </span>
                                )}
                                {sessInfo?.lieu && <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{sessInfo.lieu}</span>}
                                {!isLinked && isFicheSoumise && (
                                  <span style={{ fontSize: '0.73rem', fontWeight: 600, color: '#2563eb', background: 'rgba(59,130,246,0.1)', padding: '0.1rem 0.4rem', borderRadius: 4 }}>
                                    ⏳ Fiche soumise — en attente de validation
                                  </span>
                                )}
                                {!isLinked && !isFicheSoumise && (
                                  <span style={{ fontSize: '0.73rem', fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '0.1rem 0.4rem', borderRadius: 4 }}>
                                    ⚠ Non liée au devis — inscription désactivée
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {/* Lignes stagiaires */}
                          <div style={{ padding: '0.4rem 0.8rem' }}>
                            {stags.map(({ idx, s }) => {
                              const nom = [s.prenom, s.nom_usage || s.nom_naissance].filter(Boolean).join(' ') || `Stagiaire ${idx + 1}`;
                              const inclus = !ficheExclusions.has(idx);
                              return (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', cursor: canEnroll && (isLinked || isFicheSoumise) ? 'pointer' : 'default', opacity: canEnroll && (isLinked || isFicheSoumise) ? 1 : 0.55 }}>
                                  <input
                                    type="checkbox"
                                    checked={inclus}
                                    disabled={!canEnroll || (!isLinked && !isFicheSoumise)}
                                    onChange={e => setFicheExclusions(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.delete(idx); else next.add(idx);
                                      return next;
                                    })}
                                    style={{ width: 15, height: 15, accentColor: 'var(--accent-blue)', cursor: 'pointer' }}
                                  />
                                  <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{nom}</span>
                                  {s.date_naissance && (
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                      né(e) le {new Date(s.date_naissance).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                  {!canEnroll && <span style={{ fontSize: '0.73rem', color: '#9ca3af', fontStyle: 'italic' }}>créé sans inscription</span>}
                                  {canEnroll && isLinked && !inclus && <span style={{ fontSize: '0.73rem', color: '#f87171', fontWeight: 600 }}>exclu de l'inscription</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.8rem', alignItems: 'center' }}>
              <button
                onClick={() => ouvrirDocument({
                  titre: `Fiche d'inscription — ${devis.devis_id}`,
                  url: `/fiches-inscription/${fiche.id}/pdf`,
                  nomFichier: `fiche_inscription_${devis.devis_id}.pdf`,
                  actions: devis.lien_token ? (
                    <a href={`/fiche-inscription/${devis.lien_token}`} target="_blank" rel="noreferrer" onClick={fermerDocument}
                      style={{ display: 'inline-flex', alignItems: 'center', padding: '0.4rem 0.9rem', borderRadius: 6, background: '#7c3aed', color: '#fff', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600 }}>
                      ✏️ Modifier la fiche
                    </a>
                  ) : null,
                })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }}>
                👁 Aperçu
              </button>
              {devis.lien_token && (
                <a href={`/fiche-inscription/${devis.lien_token}`} target="_blank" rel="noreferrer"
                  title="Ouvre le formulaire en mode équipe : l'exemplaire du client est conservé dans les versions précédentes"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', textDecoration: 'none', fontSize: '0.83rem', fontWeight: 600 }}>
                  ✏️ Modifier la fiche
                </a>
              )}
              {fiche.statut === 'soumise' && (() => {
                const stagsV = (Array.isArray(fiche.stagiaires) ? fiche.stagiaires : [])
                  .filter(s => s.nom_naissance || s.nom_usage || s.prenom);
                const nbSansSession = stagsV.filter(s => !s.session_id || s.session_id === '__aucune__').length;
                const tousInconnus = stagsV.length > 0 && nbSansSession === stagsV.length;
                const partiellement = !tousInconnus && nbSansSession > 0;

                function handleValiderAvecAlerte() {
                  if (tousInconnus) {
                    if (!window.confirm(
                      `⚠️ Aucun stagiaire n'a sélectionné de session.\n\n` +
                      `Les stagiaires seront créés dans la base sans être inscrits à une session de formation.\n\n` +
                      `Voulez-vous continuer quand même ?`
                    )) return;
                  } else if (partiellement) {
                    if (!window.confirm(
                      `⚠️ ${nbSansSession} stagiaire${nbSansSession > 1 ? 's' : ''} n'ont pas sélectionné de session.\n\n` +
                      `${nbSansSession > 1 ? 'Ces stagiaires seront créés' : 'Ce stagiaire sera créé'} sans inscription à une session.\n\n` +
                      `Voulez-vous continuer ?`
                    )) return;
                  }
                  handleValiderFiche([]);
                }

                return (
                  <>
                    {(tousInconnus || partiellement) && (
                      <div style={{
                        width: '100%', padding: '0.5rem 0.85rem', borderRadius: 7, marginBottom: '0.3rem',
                        background: tousInconnus ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
                        border: `1px solid ${tousInconnus ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.35)'}`,
                        fontSize: '0.82rem', fontWeight: 600,
                        color: tousInconnus ? '#dc2626' : '#b45309',
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                      }}>
                        <span>{tousInconnus ? '🔴' : '⚠️'}</span>
                        <span>
                          {tousInconnus
                            ? 'Aucun stagiaire n\'a sélectionné de session — ils seront créés sans inscription.'
                            : `${nbSansSession} stagiaire${nbSansSession > 1 ? 's' : ''} sans session — ${nbSansSession > 1 ? 'ils seront créés' : 'il sera créé'} sans inscription.`}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={handleValiderAvecAlerte}
                      disabled={validating}
                      className="btn-primary"
                      style={{ fontSize: '0.85rem' }}>
                      {validating ? '⏳ Validation...' : '✅ Valider la fiche'}
                    </button>
                  </>
                );
              })()}
              {devis.lien_token && (
                <button
                  onClick={() => { setFicheEmailAddr(devis.email_client || ''); setShowFicheEmailModal(true); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }}>
                  ✉ Renvoyer le lien
                </button>
              )}
              <button onClick={supprimerFiche}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'transparent', border: '1px solid rgba(239,68,68,0.5)', color: '#ef4444', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}>
                🗑 Supprimer la fiche
              </button>
            </div>
          </div>
        ) : devis.statut_devis === 'Validé' ? (
          <div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginBottom: '0.8rem' }}>
              Le devis est validé mais la fiche d'inscription n'a pas encore été envoyée par le client.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {devis.lien_token && (
                <a href={`/fiche-inscription/${devis.lien_token}`} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: '#E87722', color: '#fff', textDecoration: 'none', fontSize: '0.83rem', fontWeight: 600 }}>
                  📋 Accéder à la fiche
                </a>
              )}
              {devis.lien_token && (
                <button
                  onClick={() => { setFicheEmailAddr(devis.email_client || ''); setShowFicheEmailModal(true); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }}>
                  ✉ Renvoyer le lien
                </button>
              )}
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
            La fiche d'inscription sera accessible au client après validation du devis.
          </p>
        )}
      </div>

      {/* ── Sessions liées (auto, lecture seule) ─────────────── */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 className="card-title">
          🔗 Sessions liées
          {(devis.sessions_liees?.length > 0) && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', background: 'var(--bg-elevated)', padding: '0.1rem 0.5rem', borderRadius: '4px' }}>
              {devis.sessions_liees.length}
            </span>
          )}
        </h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
          Créées automatiquement lorsque le client sélectionne une date et inscrit ses stagiaires. Ce devis apparaît dans les sessions.
        </p>

        {(devis.sessions_liees?.length > 0) ? (
          <div style={{ marginBottom: '0.8rem' }}>
            {devis.sessions_liees.map((s, i) => (
              <div key={s.session_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.8rem', borderRadius: '8px', background: i === 0 ? 'rgba(59,130,246,0.06)' : 'var(--bg-elevated)', border: `1px solid ${i === 0 ? 'rgba(59,130,246,0.25)' : 'var(--border)'}`, marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{s.session_id}</span>
                  {i === 0 && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#3b82f6', fontWeight: 600 }}>principale</span>}
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.1rem' }}>
                    {s.date_debut ? `Du ${formatDate(s.date_debut)}${s.date_fin ? ` au ${formatDate(s.date_fin)}` : ''}` : ''}
                    {s.lieu ? ` · ${s.lieu}` : ''}
                  </div>
                </div>
                <button onClick={() => navigate(`/sessions/${s.session_id}`)}
                  style={{ padding: '0.2rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--accent-blue)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                  → Voir
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            Aucune session liée pour l'instant — le client n'a pas encore sélectionné de date.
          </p>
        )}

      </div>

      {/* ── Sessions disponibles ──────────────────────────── */}
      {devis.code_formation && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">
            📅 Session(s) disponible(s)
            {devis.sessions_possibles?.length > 0 && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-tertiary)', background: 'var(--bg-elevated)', padding: '0.1rem 0.5rem', borderRadius: '4px' }}>
                {devis.sessions_possibles.length}
              </span>
            )}
          </h3>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Dates proposées au client dans le devis. Elles deviennent liées lorsqu'il sélectionne une date et inscrit ses stagiaires.
          </p>

          {/* Tableau sessions disponibles */}
          {devis.sessions_possibles?.length > 0 && (
            <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', marginBottom: '0.8rem' }}>
              {/* En-têtes */}
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 90px 72px', background: '#eef0f2', borderBottom: '1px solid #d1d5db' }}>
                {['N° session', 'Dates', 'Lieu', 'Inscrits', ''].map((h, i) => (
                  <div key={i} style={{ padding: '0.35rem 0.7rem', fontSize: '0.73rem', fontWeight: 600, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
                ))}
              </div>
              {/* Lignes */}
              {devis.sessions_possibles.map((s, i) => {
                const debut = s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '?';
                const fin   = s.date_fin   ? new Date(s.date_fin).toLocaleDateString('fr-FR')   : '?';
                return (
                  <div key={s.session_id} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 90px 72px', background: i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: i < devis.sessions_possibles.length - 1 ? '1px solid #e5e7eb' : 'none', alignItems: 'center' }}>
                    <div style={{ padding: '0.45rem 0.7rem', fontWeight: 700, fontSize: '0.85rem', color: 'var(--accent-blue)' }}>{s.session_id}</div>
                    <div style={{ padding: '0.45rem 0.7rem', fontSize: '0.82rem', color: '#374151' }}>
                      {s.date_debut ? `Du ${debut} au ${fin}` : '—'}
                    </div>
                    <div style={{ padding: '0.45rem 0.7rem', fontSize: '0.82rem', color: '#374151' }}>{s.lieu || '—'}</div>
                    <div style={{ padding: '0.45rem 0.7rem', fontSize: '0.82rem', color: '#E87722', fontWeight: 600 }}>
                      {s.nb_inscrits || 0}/{s.stagiaires_max || '?'}
                    </div>
                    <div style={{ padding: '0.3rem 0.5rem' }}>
                      {hasRole('utilisateur') && (
                        <button onClick={() => retirerSessionPossible(s.session_id)} disabled={loadingSP}
                          style={{ padding: '0.15rem 0.5rem', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#f87171', cursor: 'pointer', fontSize: '0.75rem' }}>
                          ✕ Retirer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Message disponibilité */}
          {devis.sessions_possibles?.length > 0 && (
            <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: '0.75rem' }}>
              ℹ️ Les disponibilités indiquées sont susceptibles d'évoluer.
            </p>
          )}

          {/* Dropdown pour ajouter */}
          {hasRole('utilisateur') && (() => {
            const disponibles = sessionsPossiblesDisponibles.filter(
              s => !(devis.sessions_possibles || []).some(sp => sp.session_id === s.session_id)
            );
            return (
              <div>
                <select
                  value=""
                  onChange={e => { if (e.target.value) ajouterSessionPossible(e.target.value); }}
                  disabled={loadingSP}
                  style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                  <option value="">+ Ajouter une session…</option>
                  {disponibles.map(s => {
                    const debut = s.date_debut ? new Date(s.date_debut).toLocaleDateString('fr-FR') : '?';
                    const fin = s.date_fin ? new Date(s.date_fin).toLocaleDateString('fr-FR') : '?';
                    return (
                      <option key={s.session_id} value={s.session_id}>
                        {s.session_id} — Du {debut} au {fin} ({s.nb_inscrits || 0}/{s.stagiaires_max || '?'} inscrits)
                      </option>
                    );
                  })}
                </select>
                {disponibles.length === 0 && (
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }}>
                    Aucune autre session disponible pour cette formation.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Convention de formation ──────────────────────── */}
      <div className="card" style={{ marginTop: '1rem', borderColor: convention?.statut === 'Validée' ? 'var(--accent-emerald)' : convention ? '#3b82f6' : undefined }}>
        <h3 className="card-title">
          📋 Convention de formation
          {convention && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', fontWeight: 700,
              color: convention.statut === 'Validée' ? 'var(--accent-emerald)' : convention.statut === 'Envoyée' ? '#f59e0b' : 'var(--text-secondary)',
              background: convention.statut === 'Validée' ? 'rgba(16,185,129,0.1)' : convention.statut === 'Envoyée' ? 'rgba(245,158,11,0.1)' : 'var(--bg-elevated)',
              padding: '0.15rem 0.5rem', borderRadius: '4px' }}>
              {convention.statut === 'Validée' ? '✓ Signée' : convention.statut === 'Envoyée' ? '📨 Envoyée' : '🔖 Brouillon'}
            </span>
          )}
        </h3>

        {!convention ? (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              Créez la convention à partir de ce devis{!(devis.sessions_liees?.length) ? ' (associez d\'abord une session pour inclure les infos formateur/lieu)' : ''}.
            </p>
            {hasRole('utilisateur') && (
              <button className="btn-primary" onClick={creerConvention}>📋 Créer la convention</button>
            )}
          </div>
        ) : (
          <div>
            {convention.statut === 'Validée' && (
              <div className="info-grid" style={{ marginBottom: '0.75rem' }}>
                <div className="info-row"><label>Signataire</label><span><strong>{convention.signataire_nom}</strong></span></div>
                <div className="info-row"><label>Téléphone</label><span>{convention.signataire_tel}</span></div>
                <div className="info-row"><label>Date signature</label><span>{formatDate(convention.date_signature)}</span></div>
              </div>
            )}

            {/* Boutons action */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <button className="btn-secondary" onClick={() => ouvrirApercuConvention(convention.convention_id, `Convention — ${devis.devis_id}`)}>👁 Aperçu</button>
              {hasRole('utilisateur') && convention.statut === 'Validée' && (
                <BoutonRegenerer
                  label="🔁 Nouvelle convention"
                  confirmMessage={'La convention signée sera conservée en PDF (consultable ci-dessous) et une nouvelle convention (brouillon) sera créée sur le nombre actuel de stagiaires inscrits.\n\nIl faudra la renvoyer au client pour signature.\n\nConfirmez-vous ?'}
                  onConfirm={nouvelleConvention}
                />
              )}
              {hasRole('utilisateur') && convention.statut !== 'Validée' && (
                <>
                  <BoutonRegenerer
                    confirmMessage={convention.lien_token
                      ? 'Cette convention a déjà un lien de signature.\n\nSi vous la régénérez, ce lien sera invalidé et elle repassera en brouillon : il faudra la renvoyer au client.\n\nConfirmez-vous ?'
                      : 'La convention va être recalculée à partir du devis et de sa session.\n\nConfirmez-vous ?'}
                    onConfirm={regenererConvention}
                  />
                  {convention.lien_token ? (
                    <>
                      <button className="btn-secondary" onClick={() => { navigator.clipboard.writeText(`${APP_URL}/convention-client/${convention.lien_token}`); setConvMsg('✓ Lien copié'); setTimeout(() => setConvMsg(''), 3000); }}>📋 Copier lien</button>
                      <button className="btn-primary" onClick={() => {
                        setConvEmail(devis.email_client || '');
                        const baseEnvoyer = `Bonjour,\n\nVeuillez trouver ci-dessous le lien pour consulter et signer votre convention de formation :\n\n${devis.intitule_formation}`;
                        setInclureConvocation(false);
                        setConvEmailBody(buildCorpsConvention(baseEnvoyer, false));
                        setShowConvEmailModal(true);
                      }}>✉ Envoyer</button>
                      <button onClick={() => {
                        setConvEmail(devis.email_client || '');
                        const baseRelancer = `Bonjour,\n\nNous vous relançons concernant la convention de formation en attente de signature :\n\n${devis.intitule_formation}\n\nMerci de bien vouloir signer dans les meilleurs délais.`;
                        setInclureConvocation(false);
                        setConvEmailBody(buildCorpsConvention(baseRelancer, false));
                        setShowConvEmailModal(true);
                      }}
                        style={{ padding: '0.4rem 0.9rem', borderRadius: '6px', background: 'transparent', border: '1px solid rgba(245,158,11,0.5)', color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                        🔔 Relancer
                      </button>
                    </>
                  ) : (
                    <button className="btn-primary" onClick={genererLienConventionEtEnvoyer} disabled={convSending}>
                      {convSending ? '⏳...' : '✉ Envoyer'}
                    </button>
                  )}
                </>
              )}
            </div>

            {String(convention.devis_unite || '').toLowerCase() === 'stagiaire' && convention.nb_inscrits > 0 && (() => {
              const signee = convention.statut === 'Validée';
              const qteRef = signee ? (convention.nb_stagiaires || parseFloat(convention.devis_qte)) : parseFloat(convention.devis_qte);
              if (convention.nb_inscrits === qteRef) return null;
              return signee ? (
                <p style={{ fontSize: '0.8rem', color: '#f59e0b', marginBottom: '0.5rem' }}>
                  ⚠ Convention signée sur {qteRef} stagiaire(s) — {convention.nb_inscrits} inscrit(s) aujourd'hui : générer une nouvelle convention
                </p>
              ) : (
                <p style={{ fontSize: '0.8rem', color: 'var(--accent-blue)', marginBottom: '0.5rem' }}>
                  ℹ Convention calculée sur {convention.nb_inscrits} stagiaire(s) inscrit(s) (devis : {qteRef})
                </p>
              );
            })()}

            {conventionsAnterieures.length > 0 && (
              <div style={{ marginBottom: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Conventions précédentes (PDF archivé)</div>
                {conventionsAnterieures.map(c => (
                  <div key={c.convention_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem', marginBottom: '0.25rem' }}>
                    <span>Convention {c.reference || c.convention_id}{c.nb_versions > 1 ? ` (v${c.version})` : ''}{c.statut === 'Validée' ? ` — signée le ${formatDate(c.date_signature)}` : ''}{c.nb_stagiaires ? ` — ${c.nb_stagiaires} stagiaire(s)` : ''}</span>
                    <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                      onClick={() => ouvrirDocument({ titre: `Convention ${c.reference || c.convention_id}${c.nb_versions > 1 ? ` v${c.version}` : ''} (version archivée)`, url: `/conventions/${c.convention_id}/archive`, nomFichier: `CONVENTION_${c.reference || c.convention_id}${c.nb_versions > 1 ? `_v${c.version}` : ''}.pdf` })}>
                      👁 Aperçu
                    </button>
                  </div>
                ))}
              </div>
            )}

            {autresConventions.length > 0 && (
              <div style={{ marginBottom: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Conventions des autres sessions de ce devis</div>
                {autresConventions.map(c => (
                  <div key={c.convention_id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.82rem', marginBottom: '0.25rem' }}>
                    <span>Convention {c.reference || c.convention_id}{c.nb_versions > 1 ? ` (v${c.version})` : ''} — {c.statut === 'Validée' ? `signée le ${formatDate(c.date_signature)}` : c.statut}</span>
                    {c.session_id && (
                      <button className="btn-secondary" style={{ padding: '0.15rem 0.6rem', fontSize: '0.78rem' }}
                        onClick={() => navigate(`/sessions/${c.session_id}`)}>
                        Ouvrir la session →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {convention.lien_token && convention.statut !== 'Validée' && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                Expire le {formatDate(convention.token_expire_at)}
                {convention.email_client && ` — Envoyé à : ${convention.email_client}`}
              </p>
            )}
          </div>
        )}

        {convMsg && (
          <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem',
            ...styleMessage(niveauMessage(convMsg)) }}>
            {convMsg}
          </div>
        )}
      </div>

      {/* Modal envoi email convention */}
      {showConvEmailModal && (() => {
        const aSession = (devis.sessions_liees?.length > 0) || !!devis.session_id;
        const ficheReçue = fiche?.statut === 'soumise';
        return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '540px', maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>✉ Envoyer la convention</h3>

            {/* Alertes */}
            {!ficheReçue && (
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.7rem 0.9rem', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '8px', fontSize: '0.82rem', color: '#b45309' }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                <span>La fiche d'inscription n'a pas encore été retournée par le client.</span>
              </div>
            )}
            {!aSession && (
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.7rem 0.9rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '8px', fontSize: '0.82rem', color: '#dc2626' }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                <span>Aucune session n'est associée à ce devis. La convocation ne pourra pas être générée.</span>
              </div>
            )}

            {/* Option convocation */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: aSession ? 'pointer' : 'not-allowed', padding: '0.75rem', background: aSession ? 'rgba(232,119,34,0.06)' : 'var(--bg-elevated)', borderRadius: '8px', border: `1px solid ${aSession ? 'rgba(232,119,34,0.35)' : 'var(--border)'}`, opacity: aSession ? 1 : 0.5 }}>
              <input type="checkbox" checked={inclureConvocation} onChange={e => {
                const checked = e.target.checked;
                setInclureConvocation(checked);
                setConvEmailBody(prev => buildCorpsConvention(prev, checked));
              }} disabled={!aSession}
                style={{ marginTop: '0.2rem', accentColor: '#E87722', width: 16, height: 16, cursor: aSession ? 'pointer' : 'not-allowed', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>📎 Inclure la convocation</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                  {aSession ? 'Convocation PDF + livret stagiaire joints à cet email, en plus du programme de formation (toujours joint)' : 'Aucune session associée — non disponible'}
                </div>
              </div>
            </label>

            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Email du client</label>
              <input type="email" value={convEmail} onChange={e => setConvEmail(e.target.value)} autoFocus
                style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }} />
            </div>

            {/* Message */}
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
                Message <span style={{ fontWeight: 400, color: '#94a3b8' }}>(modifiable)</span>
              </label>
              <textarea value={convEmailBody} onChange={e => setConvEmailBody(e.target.value)} rows={5}
                style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 }} />
              <p style={{ fontSize: '0.74rem', color: '#94a3b8', margin: '0.2rem 0 0' }}>Le bouton de signature et la signature sont ajoutés automatiquement.</p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => { setShowConvEmailModal(false); setInclureConvocation(false); }}>Annuler</button>
              <button className="btn-primary" onClick={envoyerEmailConvention} disabled={convSending}>{convSending ? '⏳ Envoi...' : '✉ Envoyer'}</button>
            </div>
          </div>
        </div>
        );
      })()}


      {/* Modal envoi email */}
      {showEmailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '520px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>✉ Envoyer le devis</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>Le programme de formation sera joint si disponible.</p>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Email du client</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@client.fr" autoFocus
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1rem' }} />
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
              Message <span style={{ fontWeight: 400, color: '#94a3b8' }}>(modifiable)</span>
            </label>
            <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={6}
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, marginBottom: '0.25rem' }} />
            <p style={{ fontSize: '0.74rem', color: '#94a3b8', margin: '0 0 1.2rem' }}>Le bouton d'accès au devis et la signature sont ajoutés automatiquement.</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowEmailModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={envoyerEmail} disabled={sending}>{sending ? '⏳ Envoi...' : '✉ Envoyer'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal relance */}
      {showRelanceModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '420px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>🔔 Relancer le client</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Un email de relance avec le lien de validation sera envoyé au client.
            </p>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Email du client</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="contact@client.fr" autoFocus
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1rem' }} />
            {relanceMsg && (
              <div style={{ padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '1rem',
                ...styleMessage(niveauMessage(relanceMsg)) }}>
                {relanceMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setShowRelanceModal(false)}>Annuler</button>
              <button onClick={envoyerRelance} disabled={sending}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', background: '#f59e0b', border: 'none', color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                {sending ? '⏳ Envoi...' : '🔔 Envoyer la relance'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal renvoi lien fiche d'inscription */}
      {showFicheEmailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '420px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>✉ Renvoyer la fiche d'inscription</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Le lien de la fiche d'inscription sera renvoyé à l'adresse indiquée.
            </p>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              Le client pourra modifier sa fiche une fois, même si le délai de blocage avant la session est dépassé.
            </p>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Email du destinataire</label>
            <input type="email" value={ficheEmailAddr} onChange={e => setFicheEmailAddr(e.target.value)}
              placeholder="contact@client.fr" autoFocus
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '1rem' }} />
            {ficheEnvoiMsg && (
              <div style={{ padding: '0.5rem 0.8rem', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '1rem',
                ...styleMessage(niveauMessage(ficheEnvoiMsg)) }}>
                {ficheEnvoiMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => { setShowFicheEmailModal(false); setFicheEnvoiMsg(''); }}>Annuler</button>
              <button onClick={renvoyerLienFiche} disabled={ficheEnvoyant} className="btn-primary" style={{ fontSize: '0.85rem' }}>
                {ficheEnvoyant ? '⏳ Envoi...' : '✉ Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal validation interne */}
      {showValidInterne && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowValidInterne(false); }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '2rem', width: 460, maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 0.3rem', fontSize: '1rem', fontWeight: 700 }}>✓ Validation interne du devis</h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 1.2rem' }}>
              Devis <strong>{devis.devis_id}</strong> — {devis.client_name}
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.4rem' }}>
                Origine de la validation <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={validOrigine}
                onChange={e => setValidOrigine(e.target.value)}
                rows={3}
                placeholder="Ex : Accord téléphonique de M. Dupont le 24/03/2026, Email de confirmation de Mme Martin, Bon pour accord signé en réunion…"
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '0.88rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--bg-surface)' }}
              />
            </div>

            <div style={{ padding: '0.6rem 0.9rem', borderRadius: '8px', background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)', fontSize: '0.82rem', color: '#065f46', marginBottom: '1.2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <span>📅 Date : <strong>{new Date().toLocaleDateString('fr-FR')}</strong></span>
              <span>👤 Enregistré par : <strong>{[user?.prenom, user?.nom].filter(Boolean).join(' ') || user?.login || '—'}</strong></span>
            </div>

            {validMsg && (
              <div style={{ marginBottom: '0.8rem', padding: '0.5rem 0.8rem', borderRadius: '6px', ...styleMessage(false), fontSize: '0.85rem' }}>{validMsg}</div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowValidInterne(false)}
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.88rem' }}>
                Annuler
              </button>
              <button onClick={validerInterne} disabled={validating || !validOrigine.trim()}
                style={{ padding: '0.5rem 1.3rem', borderRadius: '8px', border: 'none', background: '#059669', color: '#fff', cursor: validating || !validOrigine.trim() ? 'default' : 'pointer', fontWeight: 700, fontSize: '0.88rem', opacity: validating || !validOrigine.trim() ? 0.6 : 1 }}>
                {validating ? 'Enregistrement…' : '✓ Confirmer la validation'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal modification */}
      {modalModif && (
        <ModalModifier
          devis={devis}
          onSave={() => { setModalModif(false); reload(); }}
          onClose={() => setModalModif(false)}
        />
      )}
    </div>
  );
}
